import PouchDB from 'pouchdb-browser';
import PouchDBFind from 'pouchdb-find';
import PouchDBUpsert from 'pouchdb-upsert';
import { FetchInterceptor } from './service/FetchInterceptor';
import { customAlphabet } from 'nanoid';
import { refreshSessionOrShowAlert } from './service/interceptors';
const nanoidCustom = customAlphabet('0123456789abcdefghijklmnopqrstuvwxyz', 8);

export interface DbListener {
  onProgress?: (percent: number) => void;
  onComplete: (docs: any[]) => void;
  onChange: (event: ChangeEvent) => void;
  onError?: (error: Error) => void;
}

export interface Doc {
  _id: string;
  _rev?: string;
}

export interface Sync {
  canceled: boolean;
  pull: Replication;
  pullPaused: boolean;
  push: Replication;
  pushPaused: boolean;
  on: (name: string, event: (event: any) => void) => void;
  cancel: () => void;
}

export interface Replication {
  cancelled: boolean;
  state: string;
  on: (name: string, event: (event: any) => void) => Replication;
  catch: (error: any) => void;
  cancel: () => void;
}

export interface PutResponse {
  id: string;
  rev: string;
}

export interface PouchChange {
  change: {
    doc_write_failures: number;
    docs: any[];
    docs_read: number;
    docs_written: number;
    errors: [];
    last_seq: string;
    ok: boolean;
    pending: number;
    start_time: string;
    end_time?: string; // optional?
  };
  status: string; //"aborting"
  direction: 'push' | 'pull'; // "push" or "pull"  https://pouchdb.com/api.html#changes
}

export interface DbFindResult<T> {
  docs: T[];
  warning: string;
  bookmark?: string;
}

export interface CompleteEvent {
  docs: any[];
}

export interface ChangeEvent extends PouchChange {
  docs: any[];
}

export interface PouchError {
  dbName: string;
  error: string;
  message: string;
  name: string;
  reason: string;
  result: {
    ok: boolean;
    start_time: string;
    docs_read: number;
    docs_written: number;
    doc_write_failures: number;
    end_time: string;
    status: number;
  };
  stack: string;
  status: number;
}
export interface DbInfo {
  /** the name (and unique identifier) of the database you opened */
  db_name: string;
  /** total number of non‐deleted documents in the database */
  doc_count: number;
  /**
   * the sequence number of the database
   * starts at 0 and increments on every add/update
   */
  update_seq: number | string;
}

// TODO, switch to comparing object hashes
export function docsAreEqual(doc1, doc2) {
  const keys1 = Object.keys(doc1);
  const keys2 = Object.keys(doc2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (let key of keys1) {
    if (doc1[key] !== doc2[key]) {
      return false;
    }
  }
  return true;
}

/***
 *
      const db = MyService.getDb(this.projectId);
      db.on('complete', docs => (this.docsArray = docs));
      db.on('changes', event => (this.docsArray = event.docs));
      this.docsArray = await fileDb.getDocs();
 */
export class ConcourseDb extends PouchDB {
  static instances = {};
  static batch_size = 10;
  static getInstance(dbName): ConcourseDb {
    if (dbName.indexOf('undefined') != -1) throw new Error('Invalid dbName: ' + dbName);
    if (dbName in ConcourseDb.instances) return ConcourseDb.instances[dbName];

    const db = new ConcourseDb(dbName);
    ConcourseDb.instances[dbName] = db;
    return db;
  }

  /**
   * Get a synchronized instance of the database
   * This is slightly better because errors triggered during initial sync will be emitted.
   * @param dbName The name of the database
   * @returns A promise that resolves to the ConcourseDb instance
   */
  static async getInstanceSynced(dbName): Promise<ConcourseDb> {
    console.log(`ConcourseDb.getSyncInstance(${dbName})`);
    if (dbName.indexOf('undefined') != -1) throw new Error('Invalid dbName: ' + dbName);
    if (dbName in ConcourseDb.instances) return ConcourseDb.instances[dbName];
    const db = new ConcourseDb(dbName);
    await db.syncRemote(dbName);
    ConcourseDb.instances[dbName] = db;
    return db;
  }

  static stopInstance(dbName): void {
    const db: ConcourseDb = ConcourseDb.instances[dbName];
    if (db) {
      db.stopReplication();
      delete ConcourseDb.instances[dbName];
    }
  }

  static destroyInstance(dbName): void {
    const db: ConcourseDb = ConcourseDb.instances[dbName];
    if (db) {
      db.stopReplication();
      db.destroy();
      delete ConcourseDb.instances[dbName];
    }
  }

  static makeShortDocId(length = 8) {
    return nanoidCustom(length).toLowerCase();
  }

  static mergeChanges(docs: any[], changes: any[]): any[] {
    for (let change of changes) {
      let index = docs.findIndex(doc => doc._id == change._id);
      if (index == -1) {
        docs = [...docs, change];
      } else {
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/with
        docs = docs['with'](index, change);
      }
    }
    return docs; //.filter(doc => !doc.deleted_by);
  }

  static async getLocalInfo(dbName: string): Promise<DbInfo | null> {
    const db = new PouchDB(dbName);
    return await db.info();
  }

  /*
  static async getDoc(dbName){
    const db = ConcourseDb.getInstance(dbName)
    return await db.getDoc() // TODO: user docIsd
  }

  async getDocById(docId) {
    return await this.get(docId);
  }*/

  static getRemoteDb(dbName) {
    let url = globalThis.ccCouchdbUrl;
    if (dbName) {
      url += `/${dbName}`;
    }

    const fetchInterceptor = FetchInterceptor.getInstance('pouchdb');

    return new PouchDB(url, {
      fetch: function (url, opts?: RequestInit) {
        const headers = new Headers(opts.headers);
        headers.set('Accept', 'application/json');
        headers.set('Content-Type', 'application/json');
        return fetchInterceptor.authFetch(url as string, { ...opts, headers });
      },
      auto_compaction: true,
      skip_setup: true, // Don't try to create the database if it doesn't exist
    });
  }

  static DOCS_EVENT = 'docs';

  declare public replicate: any;
  declare public emit: any;
  declare public sync: any;
  declare public addListener: any;
  declare public removeListener: any;
  public find<T = {}>(request: PouchDB.Find.FindRequest<T>): Promise<DbFindResult<T>> {
    return super.find(request) as Promise<DbFindResult<T>>;
  }
  declare public get: any;
  declare public put: any;
  declare public post: any;
  declare public allDocs: any;

  //private docs:any[];
  declare public on: any;
  declare public bulkDocs: any;
  declare public remove: any;
  declare public getAttachment: any;
  declare public removeAttachment: any;
  declare public destroy: any;

  public docs: any[] = [];

  // Default variable to state if we are still awaiting for a 'complete' event
  public awaitingInitialSync: boolean = true;
  private initialSyncError: PouchError;
  private initialSync: Replication;
  private replicator: Sync;
  private dbName: string;

  /* constructor that also initializes live replication */
  constructor(dbName) {
    super(dbName, { auto_compaction: true });
    this.dbName = dbName;
    PouchDB.plugin(PouchDBFind);
    PouchDB.plugin(PouchDBUpsert);

    /** 
     * Handy little hack to find wherever a dev had forgotten to call syncRemote 
     * Can be removed after a few months.
    */
    setTimeout(() => {
      if (!this.initialSync) {
        console.error(`syncRemote not called on ${this.dbName}`);
      }
    }, 1000);
  }

  private isMissingRemoteDbError(error: any): boolean {
    return error?.error === 'not_found' || error?.reason === 'Database does not exist.';
  }

  /**
   * Its important to await this call so that errors get a stack trace to the calling code.
   */
  async syncRemote(dbName: string = this.dbName) {
    if (this.initialSync) {
      console.log('Initial sync already in progress');
      return;
    }

    this.docs = await this.getDocs();
    if (this.docs.length > 0) {
      this.emit('complete', this.docs); // initial event
    }
    const remoteDb = ConcourseDb.getRemoteDb(dbName);
    let totalDocs: number;
    try {
      const info = await remoteDb.info();
      totalDocs = info.doc_count;
    } catch (err) {
      if (err.error == 'unauthorized') {
        const session = await refreshSessionOrShowAlert();
        if (session) {
          const info = await remoteDb.info();
          totalDocs = info.doc_count;
        } else {
          return;
        }
      }
      if (this.isMissingRemoteDbError(err)) {
        // Some per-file DBs are created lazily; treat missing remote as empty DB.
        this.awaitingInitialSync = false;
        this.emit('complete', this.docs);
        return;
      }
      if (this.dbName.indexOf('schedule') > 0) {
        this.emit('complete', this.docs); // we are offline...
      } else {
        console.error(`Cannot fetch info() for ${dbName}:`, err);
        this.emit('error', { ...err, dbName });
        this.awaitingInitialSync = false;
      }
    }
    let pulled = 0;
    // First we try download all changes from remote
    this.initialSync = this.replicate.from(remoteDb);
    this.initialSync
      .on('change', info => {
        pulled += info.docs.length;
        if (totalDocs > 0) {
          this.emit('progress', Math.min(1, pulled / totalDocs));
        }
      })
      .on('complete', async () => {
        this.awaitingInitialSync = false;
        if ((this as any)._destroyed || (this as any)._closed) return;
        const docs = await this.getDocs();
        const changedDocs = this.deltaDocs(this.docs, docs);

        // I am experimenting with dispatching a complete event as soon as the initial sync is complete.
        if (this.docs.length == 0 || changedDocs.length > 0 || this.dbName.indexOf('schedule') > 0) {
          this.docs = docs;
          this.emit('complete', docs);
          this.emit(ConcourseDb.DOCS_EVENT, docs);
        }

        // start live sync that will push changes up to remote
        // and listen to changes
        this.replicator = this.sync(remoteDb, {
          live: true,
          retry: true,
        });

        // listen to events on live replication
        const proxyEvent = name => {
          this.replicator.on(name, event => {
            this.emit(name, event);
          });
        };
        proxyEvent('create');
        proxyEvent('update');
        proxyEvent('delete');

        this.replicator.on('change', (event: PouchChange) => {
          this.docs = ConcourseDb.mergeChanges(this.docs, event.change.docs);
          const ccEvent: ChangeEvent = { ...event, docs: this.docs };
          this.emit('change', ccEvent);
          if (event.direction == 'pull') {
            this.emit(ConcourseDb.DOCS_EVENT, this.docs);
          }
        });

        this.replicator.on('error', event => {
          if (event.message == 'Failed to fetch') {
            console.log('Failed to fetch, probably offline');
          } else {
            this.emit('error', event);
          }
        });

        // now we have started the more permanent sync, we cancel the first one.
        this.initialSync.cancel();
      })
      .catch(error => {
        if (this.isMissingRemoteDbError(error)) {
          this.awaitingInitialSync = false;
          this.emit('complete', this.docs);
          return;
        }
        console.error('Replication error v2: ' + dbName, error);
        this.initialSyncError = error;
        this.emit('error', { ...error, dbName });
        this.awaitingInitialSync = false;
      });
  }

  stopReplication() {
    if (this.initialSync) {
      this.initialSync.cancel();
    }
    if (this.replicator) {
      this.replicator.cancel();
    }
  }

  deltaDocs(original: any[], incoming: any[]) {
    return incoming.filter(doci => !original.find(doco => doco._id == doci._id && doco._rev == doci._rev));
  }

  async getDoc(id: string) {
    return await this.getDocById(id);
  }

  async getDocs() {
    if ((this as any)._destroyed || (this as any)._closed) return [];
    let response = await this.allDocs({
      include_docs: true,
      attachments: true,
    });

    // We return an error if this is not simply one of being offline, in which case we would want the offline docs.
    if (!response.rows && this.initialSyncError && this.initialSyncError.message != 'Failed to fetch') {
      this.emit('error', this.initialSyncError);
    }

    // we don't want to show deleted docs
    const docs = response.rows.filter(row => !row.id.startsWith('_')).map(row => row.doc);
    return docs;
  }

  /**
   * This method is for getting docs that are required for the UI to render.
   * It will hang awaiting a sync, so not ideal in offline situation.
   *
   * Its also problematic if data has changed on the server since you last synced.
   * The user will get a cached version and updates will be ignored.
   *
   * I suggest use it sparingly, and only for data that changes infrequently.
   * @returns
   */
  async getDocsAwaitSync(): Promise<any[]> {
    const docs = await this.getDocs();
    return new Promise((resolve, reject) => {
      // Docs found locally, lets resolve immediately
      if (docs && docs.length > 0) {
        resolve(docs);
      }

      // No docs found and the sync is already complete.
      if (!this.awaitingInitialSync) {
        reject(`No Docs exist`);
      }

      if (this.initialSyncError) {
        reject(this.initialSyncError);
      }

      // Not enough records found, await sync or error
      this.addListener('complete', docs => {
        resolve(docs);
      });

      this.addListener('error', err => {
        reject(`Error loading Doc` + err.message);
      });
    });
  }

  async getDocById(docId: string): Promise<any> {
    try {
      return await this.get(docId, { attachments: true });
    } catch (e) {
      // this error is thrown on first load of a db when its empty
      if (e.name == 'not_found') {
        return null;
      }
      console.error(e);
      throw e;
    }
  }

  /** PLEASE DO NOT USE */
  /** @deprecated this method will cause the ui to hang if the user is offline */
  async getDocByIdAwaitSync(docId: string): Promise<any> {
    const doc = await this.getDocById(docId);
    return new Promise((resolve, reject) => {
      // Docs found locally, lets resolve immediately
      if (doc) {
        resolve(doc);
      }

      // No docs found and the sync is already complete.
      if (!this.awaitingInitialSync) {
        reject(`No Doc exist`);
      }

      // there was an error during initial sync, lets reject
      if (this.initialSyncError) {
        reject(this.initialSyncError);
      }

      // Not enough records found, await sync or error
      this.addListener('complete', (docs: any[]) => {
        resolve(docs.find(d => d._id == docId));
      });

      this.addListener('error', err => {
        reject(`Error loading Doc` + err.message);
      });

      setTimeout(() => {
        reject('Timeout loading doc');
      }, 10000);
    });
  }

  async putAttachment(docId, attachmentId, attachment, content_type): Promise<any> {
    try {
      const doc = await this.get(docId);

      const payload = {
        _id: docId,
        _rev: doc._rev,
        _attachments: {
          [attachmentId]: {
            content_type,
            data: attachment,
          },
        },
      };

      return await this.put(payload);
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async getAttachmentUrl(docId, attachmentName) {
    try {
      const blob = await this.getAttachment(docId, attachmentName);
      return URL.createObjectURL(blob);
    } catch (e) {
      // do nothing, probably just not found
      return null;
    }
  }

  async insert(doc): Promise<any> {
    return this.saveDoc(doc);
  }

  async update(doc): Promise<any> {
    return this.saveDoc(doc);
  }

  async upsert(doc): Promise<any> {
    return this.saveDoc(doc);
  }

  async saveDoc(doc): Promise<any> {
    let response: PutResponse;
    if (doc._id) {
      try {
        // Fetch the existing document from the database
        const existingDoc = await this.get(doc._id);

        // Update the _rev property of the doc object with the retrieved revision
        doc._rev = existingDoc._rev;
      } catch (e) {
        console.log(e);
      }

      // Save the updated document
      response = await this.put(doc);
    } else {
      response = await this.post(doc);
    }

    return new Promise(resolve => {
      resolve({ ...doc, _rev: response.rev, _id: response.id });
    });
  }

  async saveDocs(docs: any): Promise<Doc[]> {
    const result: { id: string; ok: boolean; rev: string }[] = await this.bulkDocs(docs);
    return docs.map((doc, index) => {
      if (doc._id) {
        return { ...doc, _rev: result.find(r => r.id == doc._id).rev };
      } else {
        return { ...doc, _id: result[index].id, _rev: result[index].rev };
      }
    });
  }

  /**
   * Save a document and sync it to the remote database
   */
  async awaitSync(): Promise<void> {
    const remoteDb = ConcourseDb.getRemoteDb(this.dbName);
    await this.replicate.to(remoteDb);
  }

  addListeners(listener: DbListener) {
    if (listener.onProgress) {
      this.addListener('progress', listener.onProgress);
    }
    this.addListener('complete', listener.onComplete);
    this.addListener('change', listener.onChange);
    if (listener.onError) {
      this.addListener('error', listener.onError);
    }

    // If sync already completed before this listener was added, replay the event immediately
    if (!this.awaitingInitialSync) {
      listener.onComplete(this.docs);
    }
  }

  removeListeners(listener: DbListener) {
    if (listener.onProgress) {
      this.removeListener('progress', listener.onProgress);
    }
    this.removeListener('complete', listener.onComplete);
    this.removeListener('change', listener.onChange);
    if (listener.onError) {
      this.removeListener('error', listener.onError);
    }
  }

  destroyDb() {
    this.stopReplication();
    return this.destroy();
  }
}
